import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Countdown from 'react-countdown';
import api from '../api/client';
import ProductCard from '../components/ProductCard';
import Toast, { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import './BidCentrePage.css';

const STATUS_LABELS = {
  active: { label: 'Leading', color: 'var(--color-success)' },
  overbid_internal: { label: 'Outbid', color: 'var(--color-error)' },
  overbid_external: { label: 'Outbid (Japan)', color: 'var(--color-error)' },
  won: { label: 'Won', color: 'var(--color-gold)' },
  lost: { label: 'Lost', color: 'var(--color-text-muted)' },
};

function CountdownRenderer({ days, hours, minutes, seconds, completed }) {
  if (completed) return <span style={{ color: 'var(--color-text-muted)' }}>Ended</span>;
  if (days > 0) return <span>Closing in {days} {days === 1 ? 'day' : 'days'} {hours}h</span>;
  return <span style={{ color: 'var(--color-error)' }}>Closing in {hours}h {minutes}m</span>;
}

function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const displayText = selected.length === 0 ? label : `${label} (${selected.length})`;

  return (
    <div className="multi-select" onMouseLeave={() => setOpen(false)}>
      <button className="input multi-select-btn" onClick={() => setOpen(!open)}>
        {displayText} <span className="multi-chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="multi-dropdown">
          {options.map(opt => (
            <label key={opt} className="multi-option">
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
              <span>{opt.charAt(0).toUpperCase() + opt.slice(1)}</span>
            </label>
          ))}
          {options.length === 0 && <div className="multi-empty">No options</div>}
        </div>
      )}
    </div>
  );
}

function BidsTab({ bids, navigate, isPast }) {
  if (bids.length === 0) {
    return <p style={{ color: 'var(--color-text-muted)' }}>No bids to show.</p>;
  }
  return (
    <div className="bids-list">
      {bids.map(bid => {
        const statusInfo = STATUS_LABELS[bid.status] || { label: bid.status, color: 'inherit' };
        let firstImage = null;
        try {
          const imgs = typeof bid.images === 'string' ? JSON.parse(bid.images) : bid.images;
          firstImage = imgs?.[0];
        } catch {}
        return (
          <div key={bid.id} className="bid-row" onClick={() => navigate(`/products/${bid.product_id}`)}>
            <div className="bid-image">
              {firstImage ? <img src={firstImage} alt="" /> : <div className="bid-no-img" />}
            </div>
            <div className="bid-details">
              <div className="bid-brand">{bid.brand}</div>
              <div className="bid-title">{bid.clean_title}</div>
              <div className="bid-amount">Your bid: &euro;{bid.amount_euro?.toLocaleString('fr-FR')}</div>
              {isPast && bid.winning_price_euro && (
                <div className="bid-final-price">Final price: &euro;{Math.ceil(bid.winning_price_euro).toLocaleString('fr-FR')}</div>
              )}
            </div>
            <div className="bid-right">
              <span style={{ color: statusInfo.color, fontWeight: 500, fontSize: 13 }}>{statusInfo.label}</span>
              {!isPast && bid.auction_date_end_france && (
                <Countdown date={new Date(bid.auction_date_end_france)} renderer={CountdownRenderer} />
              )}
              {isPast && !bid.auction_result && (
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Checking result...</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WishlistTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const [hideTarget, setHideTarget] = useState(null);
  const [filters, setFilters] = useState({ brand: [], color: [], grade: [], product_type: [], search: '' });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['wishlist'],
    queryFn: () => api.get('/wishlist').then(r => r.data),
  });
  const { data: wishlistIds = [] } = useQuery({
    queryKey: ['wishlist-ids'],
    queryFn: () => api.get('/wishlist/ids').then(r => r.data),
  });

  const toggle = useMutation({
    mutationFn: (id) => {
      if (wishlistIds.includes(id)) return api.delete(`/wishlist/${id}`);
      return api.post('/wishlist', { product_id: id });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wishlist'] }); qc.invalidateQueries({ queryKey: ['wishlist-ids'] }); },
  });

  const placeBid = useMutation({
    mutationFn: ({ productId, amount }) => api.post('/bids', { product_id: productId, amount_euro: amount }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wishlist'] });
      showToast('Your bid has been placed successfully.');
    },
  });

  const hideProduct = useMutation({
    mutationFn: (productId) => api.post(`/admin/products/${productId}/hide`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wishlist'] });
      setHideTarget(null);
    },
  });

  const saveNotes = useMutation({
    mutationFn: ({ productId, notes }) => api.patch(`/wishlist/${productId}/notes`, { notes }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wishlist'] }); showToast('Note saved.'); },
  });

  const filterOptions = useMemo(() => {
    const brands = [...new Set(items.map(i => i.brand).filter(Boolean))].sort();
    const colors = [...new Set(items.map(i => i.color).filter(Boolean))].sort();
    const types = [...new Set(items.map(i => i.product_type).filter(Boolean))].sort();
    return { brands, colors, types };
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter(p => {
      if (filters.brand.length && !filters.brand.includes(p.brand)) return false;
      if (filters.color.length && !filters.color.includes(p.color)) return false;
      if (filters.grade.length && !filters.grade.includes(p.grade_standardized)) return false;
      if (filters.product_type.length && !filters.product_type.includes(p.product_type)) return false;
      if (filters.search) {
        const term = filters.search.toLowerCase();
        const blob = `${p.clean_title || ''} ${p.brand || ''}`.toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });
  }, [items, filters]);

  const hasFilters = filters.brand.length || filters.color.length || filters.grade.length || filters.product_type.length || filters.search;

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>;

  return (
    <div>
      {items.length > 0 && (
        <div className="wishlist-controls">
          <input
            className="input search-input"
            placeholder="Search brand, model..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
          <div className="filters">
            <MultiSelect label="All brands" options={filterOptions.brands} selected={filters.brand} onChange={v => setFilters(f => ({ ...f, brand: v }))} />
            <MultiSelect label="All colors" options={filterOptions.colors} selected={filters.color} onChange={v => setFilters(f => ({ ...f, color: v }))} />
            <MultiSelect label="All grades" options={['S', 'A', 'AB', 'B', 'BC', 'C', 'D']} selected={filters.grade} onChange={v => setFilters(f => ({ ...f, grade: v }))} />
            <MultiSelect label="All categories" options={filterOptions.types} selected={filters.product_type} onChange={v => setFilters(f => ({ ...f, product_type: v }))} />
            {hasFilters && (
              <button className="btn btn-outline btn-sm" onClick={() => setFilters({ brand: [], color: [], grade: [], product_type: [], search: '' })}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {items.length === 0 && <p style={{ color: 'var(--color-text-muted)' }}>Your wishlist is empty.</p>}
      {items.length > 0 && filteredItems.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>No items match your filters.</p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
        {filteredItems.map(p => (
          <ProductCard
            key={p.id}
            product={p}
            isWishlisted={wishlistIds.includes(p.id)}
            onToggleWishlist={id => toggle.mutate(id)}
            onQuickBid={(productId, amount) => placeBid.mutate({ productId, amount })}
            isAdmin={user?.is_admin}
            onHide={id => setHideTarget(id)}
            showNotes={true}
            notesValue={p.notes || ''}
            onSaveNotes={(productId, notes) => saveNotes.mutate({ productId, notes })}
            userType={user?.user_type || 'individual'}
          />
        ))}
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <ConfirmDialog
        open={!!hideTarget}
        title="Hide this item?"
        message="Sure you want to hide this item? It will no longer appear in the feed until you unhide it."
        confirmLabel="Hide"
        onConfirm={() => hideTarget && hideProduct.mutate(hideTarget)}
        onCancel={() => setHideTarget(null)}
      />
    </div>
  );
}

export default function BidCentrePage({ initialTab = 'current' }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(sessionStorage.getItem('bid-centre-tab') || initialTab);
  const changeTab = (tab) => { setActiveTab(tab); sessionStorage.setItem('bid-centre-tab', tab); };

  const { data: bids = [], isLoading: bidsLoading } = useQuery({
    queryKey: ['my-bids'],
    queryFn: () => api.get('/bids/my-bids').then(r => r.data),
  });

  const now = new Date();
  const currentBids = bids.filter(b => {
    if (!b.auction_date_end_france) return false;
    return new Date(b.auction_date_end_france) > now;
  });
  const pastBids = bids.filter(b => {
    if (!b.auction_date_end_france) return true;
    return new Date(b.auction_date_end_france) <= now;
  });

  return (
    <div className="bid-centre-page">
      <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 400, marginBottom: 24 }}>Bid Centre</h1>

      <div className="bid-centre-tabs">
        <button
          className={`bid-centre-tab ${activeTab === 'current' ? 'active' : ''}`}
          onClick={() => changeTab('current')}
        >
          Your Current Bids <span className="bid-centre-tab-count">{currentBids.length}</span>
        </button>
        <button
          className={`bid-centre-tab ${activeTab === 'past' ? 'active' : ''}`}
          onClick={() => changeTab('past')}
        >
          Past Bids <span className="bid-centre-tab-count">{pastBids.length}</span>
        </button>
      </div>

      {bidsLoading && (activeTab === 'current' || activeTab === 'past') && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
      )}

      {!bidsLoading && activeTab === 'current' && (
        <BidsTab bids={currentBids} navigate={navigate} isPast={false} />
      )}
      {!bidsLoading && activeTab === 'past' && (
        <BidsTab bids={pastBids} navigate={navigate} isPast={true} />
      )}
    </div>
  );
}
